/**
 * Web component: Hero teaser (hero-teaser)
 * -----------------------------------------------------------------------------
 * Hero teaser with scheduled displays and copyable promo code.
 *
 */
import './hero-teaser.scss';

import { vueReady } from '~/core/runners';
import {
  checkTeaserTimeRemaining,
  handleCopy,
  handleTrackingCampaign,
  teaserClosestToEndTime,
  updateTeasers,
} from '~/helpers/teasers';

/**
 * DOM selectors.
 */
const selectors = {
  teaserLink: '[js-hero-teaser="link"]',
  copyButton: '[js-hero-teaser="copyButton"]',
  fallback: '[js-hero-teaser="fallback"]',
  teaser: '[js-hero-teaser="teaser"]',
  tooltip: '[js-hero-teaser="tooltip"]',
  promoText: '[js-hero-teaser="promoText"]',
};

/**
 * Create new <hero-teaser> custom element.
 */
class HeroTeaser extends HTMLElement {
  data(component) {
    return {
      selectors: {
        fallback: component.querySelector(selectors.fallback),
        teasers: [...component.querySelectorAll(selectors.teaser)],
        promoTexts: component.querySelectorAll(selectors.promoText),
        copyButtons: component.querySelectorAll(selectors.copyButton),
      },
    };
  }

  /**
   * Component lifecycle - before mount.
   */
  constructor() {
    super();
    this.$data = this.data(this);
    this.teasersUpdated = false;

    // for initial load, read campaign state from local storage and update
    // teasers if necessary
    const { copyButtons } = this.$data.selectors;
    if (copyButtons) {
      const discountCampaigns = JSON.parse(
        localStorage.getItem('discount-campaigns'),
      );

      updateTeasers.call(this, discountCampaigns, selectors);
      // listen to campaign changes, update teaser content
      cnvs.EventBus.listen('discount-campaign', (campaigns) => {
        updateTeasers.call(this, campaigns, selectors);
      });
    }
  }

  /**
   * Component lifecycle - mounted.
   */
  connectedCallback() {
    this.$data = Object.keys(this.$data).length ? this.$data : this.data(this);

    const { teasers } = this.$data.selectors;
    const selectedTeaser = teaserClosestToEndTime(teasers, null);

    // Tracking code, trigger view when selectedTeaser is in view
    if (selectedTeaser) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleTrackingCampaign(
              selectedTeaser,
              'Campaign teaser',
              'view_promotion',
            );
            observer.disconnect();

            // Add click event listener after view event
            const selectedTeaserLink = selectedTeaser.querySelector(
              selectors.teaserLink,
            );
            if (
              selectedTeaserLink &&
              !selectedTeaserLink.classList.contains('js-hero-teaser-link')
            ) {
              selectedTeaserLink.addEventListener('click', () => {
                handleTrackingCampaign(
                  selectedTeaser,
                  'Campaign teaser',
                  'select_promotion',
                );
              });
              selectedTeaserLink.classList.add('js-hero-teaser-link');
            }
          }
        });
      });

      observer.observe(selectedTeaser);
    }

    this.addEventListener('click', (event) => handleCopy(event));

    if (selectedTeaser) {
      setInterval(() => {
        checkTeaserTimeRemaining.call(this, selectedTeaser, selectors);
      }, 1000);
    }
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('hero-teaser')) {
    return;
  }

  customElements.define('hero-teaser', HeroTeaser);
});
